/// <amd-module name="typeahead/features/AkamaiLocationInjector" />
import {ClientHandlerRegistry} from "typeahead/client/ClientHandlerRegistry";
import {Experiment} from "typeahead/core/Experiment";
import {IExperimentalFeature} from "typeahead/core/IExperimentalFeature";
import {ProblemOrigin} from "typeahead/core/logging/ProblemOrigin";
import {Severity} from "typeahead/core/logging/Severity";
import TypeaheadErrorLogger from "typeahead/core/logging/TypeaheadErrorLogger";
import {FormLoadedMessage} from "typeahead/core/message/FormLoadedMessage";
import {OriginPopulationMessage} from "typeahead/core/message/OriginPopulationMessage";
import {TypeaheadMessage} from "typeahead/core/message/TypeaheadMessage";
import * as ExecutionHelper from "typeahead/core/plugins/ExecutionHelper";
import * as SearchParametersUpdater from "typeahead/core/SearchParametersUpdater";
import * as StripingConfiguration from "typeahead/core/StripingConfiguration";
import {IStripingConfiguration} from "typeahead/core/StripingConfiguration";
import * as SuggestionFactory from "typeahead/core/SuggestionFactory";
import {TypeaheadUtils} from "typeahead/core/TypeaheadUtils";

/**
 * Created by matterickson on 7/18/17.
 */

const configuration: IStripingConfiguration[] = [
    {
        clients: ["Homepage", "FlightsLandingPage"],
        experiment: {
            id: 12842,
            name: "CWTA_DefaultCurrentLocationAkamai",
            value: 2,
        },
        tabs: ["flight"],
    },
];

export class AkamaiLocationInjector implements IPageLoadHandler, IExperimentalFeature {

    public FLIGHT_ORIGIN_SELECTOR: string = "input[data-provide='utypeahead'][data-lob='FLIGHTS'][id*='flight-origin']";
    private ESS_NEARBY_API: string = "/api/v4/nearby";

    public handlePageLoad(options: any, context: IContext): JQueryPromise<any> {
        let deferred: JQueryDeferred<any> = $.Deferred();
        if (!context.tab) {
            this.makeRequests(context);
        } else {
            TypeaheadMessage.subscribe(FormLoadedMessage.getTopic(), (topic, message) => {
                this.makeRequests(context);
            });
        }
        deferred.resolve(undefined);
        return deferred.promise();
    }

    public getExperiments(): Experiment[] {
        return StripingConfiguration.getExperiments(configuration);
    }

    private makeRequests(context: IContext) {
        context.tab = ClientHandlerRegistry.getTab();

        ExecutionHelper.shouldExecute(context, configuration).done((shouldExecute) => {
            if (shouldExecute) {
                this.makeNearbyRequest(context).done((response) => {
                    this.populateOriginAirport(response);
                }).fail(() => {
                    TypeaheadErrorLogger.log(new Error("Failed to get nearby airport"),
                        Severity.Excusable, ProblemOrigin.ExpediaSuggestService);
                });
            }
        }).fail(() => {
            TypeaheadErrorLogger.log(new Error("Failed to evaluate exp 12842"),
                Severity.Excusable, ProblemOrigin.DataCaptureToolkit);
        });
    }

    private makeNearbyRequest(context: IContext): JQueryPromise<any> {
        let deferred: JQueryPromise<any> = $.Deferred();
        try {
            let coords: any = this.getAkamaiCoordinates();
            if (coords && coords.latitude && coords.longitude) {
                deferred = $.ajax({
                    data: {
                        client: TypeaheadUtils.getClientId(),
                        debug: true,
                        latlong: coords.latitude + "|" + coords.longitude,
                        locale: TypeaheadUtils.getLocale(),
                        maxradius: 200,
                        maxresults: 20,
                        regiontype: 1,
                        siteid: TypeaheadUtils.getSiteId(),
                    },
                    dataType: "jsonp",
                    timeout: 2000,
                    type: "GET",
                    url: context.serviceUrl + this.ESS_NEARBY_API,
                });
            } else {
                deferred.fail();
            }
        } catch (error) {
            deferred.fail();
        }
        return deferred;
    }

    private getAkamaiCoordinates() {
        // note: coords only set in production. for testing, return an object with lat/long
        if (window.utag_data && window.utag_data.context && window.utag_data.context.clientInfo) {
            return window.utag_data.context.clientInfo.coords;
        }
        return null;
    }

    private populateOriginAirport(response: any) {
        let airports: any[] = response.sr;
        let bestAirport: any = this.pickBestAirport(airports);
        if (bestAirport) {
            let suggestion = SuggestionFactory.createSuggestion(bestAirport);
            let airportName = suggestion.getFullName().orElse("");
            let $flightInput = $(this.FLIGHT_ORIGIN_SELECTOR);
            if ($flightInput && $flightInput.length > 0) {
                $flightInput.val(airportName);
                SearchParametersUpdater.updateForSuggestion(
                    airportName, suggestion, $flightInput.data(), $flightInput.attr("id"));
                TypeaheadMessage.publish(new OriginPopulationMessage());
            }
        }
    }

    private pickBestAirport(airports: any[]) {

        function getAirportScore(airport: any) {
            if (airport && airport.POPULARITY && airport.hierarchyInfo && airport.hierarchyInfo.nearby) {
                let distance: number = airport.hierarchyInfo.nearby.distInMI;
                if (distance === 0) {
                    distance = 0.0001;
                }
                return parseFloat(airport.POPULARITY) / Math.pow(distance, 2);
            }
            return 0;
        }

        let bestScore: number = -1;
        let bestAirport = null;

        if (airports && airports.length > 0) {
            airports.forEach((airport: any): void => {
                let score: number = getAirportScore(airport);
                console.warn(airport.regionNames.displayName + ": " + score);
                if (score > bestScore) {
                    bestScore = score;
                    bestAirport = airport;
                }
            });
        }
        return bestAirport;
    }

}
